import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type LocationCoords = {
  lat: number;
  lng: number;
};

export type SetCoordsPayload = LocationCoords & {
  locationLabel?: string;
  searchZipCode?: string | null;
};

export type LocationSearchState = {
  coords: LocationCoords | null;
  locationLabel: string | null;
  /** Resolved ZIP for the searched location; used to seed find-trials for guests. */
  searchZipCode: string | null;
  sortByDistance: boolean;
  selectedCommunities: string[];
  nationwideTotal: number;
  communities: string[];
  hasSearched: boolean;
  searchResetVersion: number;
  /** Distance filter lives here (not in component state) so it survives SPA navigation. */
  distanceLabel: string | null;
  distanceMiles: number | null;
  /** True only after the site detail locality breadcrumb is clicked. */
  returnFromSiteBreadcrumb: boolean;
  /** Reset version whose facet/filter clearing was already applied; survives resetSearch. */
  consumedSearchResetVersion: number;
};

const toSearchZipCode = (value?: string | null): string | null => {
  if (value === undefined || value === null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed || null;
};

const initialState: LocationSearchState = {
  coords: null,
  locationLabel: null,
  searchZipCode: null,
  sortByDistance: false,
  selectedCommunities: [],
  nationwideTotal: 0,
  communities: [],
  hasSearched: false,
  searchResetVersion: 0,
  distanceLabel: null,
  distanceMiles: null,
  returnFromSiteBreadcrumb: false,
  consumedSearchResetVersion: 0,
};

const areSameCommunities = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const locationSearchSlice = createSlice({
  name: 'locationSearch',
  initialState,
  reducers: {
    setCoords: (state, action: PayloadAction<SetCoordsPayload>) => {
      state.coords = { lat: action.payload.lat, lng: action.payload.lng };
      state.locationLabel = action.payload.locationLabel?.trim() || null;
      state.searchZipCode = toSearchZipCode(action.payload.searchZipCode);
      state.sortByDistance = true;
      state.hasSearched = true;
    },
    setLocationLabel: (state, action: PayloadAction<string | null | undefined>) => {
      const next = action.payload?.trim();
      state.locationLabel = next || null;
    },
    markSiteBreadcrumbReturn: (state) => {
      state.returnFromSiteBreadcrumb = true;
    },
    consumeSiteBreadcrumbReturn: (state) => {
      state.returnFromSiteBreadcrumb = false;
      state.locationLabel = null;
    },
    toggleCommunity: (state, action: PayloadAction<string>) => {
      const next = action.payload;
      const index = state.selectedCommunities.indexOf(next);
      if (index >= 0) {
        state.selectedCommunities.splice(index, 1);
        return;
      }
      state.selectedCommunities.push(next);
    },
    clearCommunity: (state) => {
      state.selectedCommunities = [];
    },
    setNationwideTotal: (state, action: PayloadAction<number>) => {
      if (state.nationwideTotal > 0 || action.payload <= 0) {
        return;
      }
      state.nationwideTotal = action.payload;
    },
    setCommunities: (state, action: PayloadAction<string[]>) => {
      if (state.selectedCommunities.length > 0) {
        return;
      }
      if (areSameCommunities(state.communities, action.payload)) {
        return;
      }
      state.communities = action.payload;
    },
    setDistanceFilter: (
      state,
      action: PayloadAction<{ label: string | null; miles: number | null }>
    ) => {
      state.distanceLabel = action.payload.label;
      state.distanceMiles = action.payload.miles;
    },
    clearLocationContext: (state) => {
      state.coords = null;
      state.locationLabel = null;
      state.searchZipCode = null;
      state.sortByDistance = false;
      state.hasSearched = false;
      state.distanceLabel = null;
      state.distanceMiles = null;
      state.returnFromSiteBreadcrumb = false;
    },
    resetSearch: (state) => ({
      ...initialState,
      nationwideTotal: state.nationwideTotal,
      searchResetVersion: state.searchResetVersion + 1,
      consumedSearchResetVersion: state.consumedSearchResetVersion,
    }),
    markSearchResetConsumed: (state) => {
      state.consumedSearchResetVersion = state.searchResetVersion;
    },
  },
});

export const {
  setCoords,
  setLocationLabel,
  markSiteBreadcrumbReturn,
  consumeSiteBreadcrumbReturn,
  toggleCommunity,
  clearCommunity,
  setNationwideTotal,
  setCommunities,
  setDistanceFilter,
  clearLocationContext,
  resetSearch,
  markSearchResetConsumed,
} = locationSearchSlice.actions;

export default locationSearchSlice.reducer;
